import { Fragment } from 'react';
import { ChevronDown, ChevronRight, ExternalLink, Car, Check, X } from 'lucide-react';
import { DOC_KEYS, STATE_STYLE, fmtDate, daysText, profileUrl } from './utils';
import { RowEditor } from './RowEditor';

function DocCell({ doc, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} className={`w-full min-w-[118px] rounded-lg border px-2 py-1 text-left transition-transform hover:scale-[1.02] ${STATE_STYLE[doc.state]}`} title="Tarihi düzenlemek için tıklayın" data-testid={testId}>
      <div className="flex items-center gap-1 text-[11px] font-semibold">
        {doc.uploaded ? <Check className="h-3 w-3 text-emerald-600" /> : <X className="h-3 w-3 text-red-500" />}
        {fmtDate(doc.date)}
      </div>
      <div className="text-[10px] opacity-80">{daysText(doc.days)}</div>
    </button>
  );
}

function Completion({ pct }) {
  const color = pct === 100 ? 'bg-emerald-500' : pct >= 70 ? 'bg-sky-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden"><div className={`h-full ${color}`} style={{ width: `${pct}%` }} /></div>
      <span className="text-xs font-bold tabular-nums w-9 text-right">{pct}%</span>
    </div>
  );
}

const STATUS_BADGE = {
  active: 'bg-emerald-100 text-emerald-700',
  passive: 'bg-slate-200 text-slate-600',
  left: 'bg-red-100 text-red-700',
};
const STATUS_LABEL = { active: 'Aktif', passive: 'Pasif', left: 'Ayrıldı' };

export function OverviewTable({ rows, expanded, onToggle, onSaved }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-x-auto" data-testid="overview-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800/60 text-[11px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="w-8" />
            <th className="text-left px-3 py-2">Çalışan</th>
            <th className="text-left px-3 py-2">Araç</th>
            {DOC_KEYS.map((d) => <th key={d.key} className="text-left px-2 py-2">{d.label}</th>)}
            <th className="text-left px-3 py-2">Eksikler</th>
            <th className="text-left px-3 py-2">Tamamlanma</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const open = expanded === r.user_id;
            return (
              <Fragment key={r.user_id}>
                <tr className={`border-t border-slate-100 dark:border-slate-800 ${open ? 'bg-sky-50/60 dark:bg-sky-900/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800/40'}`} data-testid={`overview-row-${r.user_id}`}>
                  <td className="pl-3">
                    <button type="button" onClick={() => onToggle(r.user_id)} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700" data-testid={`overview-expand-${r.user_id}`}>
                      {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => onToggle(r.user_id)} className="text-left">
                      <div className="font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap">{r.full_name}</div>
                      <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                        <span className={`rounded px-1 font-semibold ${STATUS_BADGE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                        {r.firm_name}{r.employment_end_date ? ` · ${fmtDate(r.employment_end_date)}` : ''}
                      </div>
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    {r.vehicles.length ? r.vehicles.map((v) => (
                      <a key={v.vehicle_id} href={`/dashboard/vehicles/${v.vehicle_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mr-1 mb-0.5 rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[11px] font-mono font-semibold hover:bg-sky-100">
                        <Car className="h-3 w-3" />{v.plate}
                      </a>
                    )) : <span className="text-xs text-slate-300">—</span>}
                  </td>
                  {DOC_KEYS.map((d) => (
                    <td key={d.key} className="px-2 py-2"><DocCell doc={r.documents[d.key]} onClick={() => onToggle(r.user_id, true)} testId={`overview-doc-${d.key}-${r.user_id}`} /></td>
                  ))}
                  <td className="px-3 py-2 text-[11px]">
                    {r.missing_docs.length > 0 && <div className="text-red-600 font-semibold">{r.missing_docs.length} belge</div>}
                    {r.missing_fields.length > 0 && <div className="text-amber-600">{r.missing_fields.length} bilgi</div>}
                    {!r.missing_docs.length && !r.missing_fields.length && <span className="text-emerald-600">—</span>}
                  </td>
                  <td className="px-3 py-2"><Completion pct={r.completion} /></td>
                  <td className="px-3 py-2">
                    <a href={profileUrl(r.user_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-sky-600 hover:underline whitespace-nowrap" data-testid={`overview-profile-${r.user_id}`}>
                      Profil <ExternalLink className="h-3 w-3" />
                    </a>
                  </td>
                </tr>
                {open && (
                  <tr><td colSpan={DOC_KEYS.length + 6} className="p-0"><RowEditor row={r} onSaved={onSaved} /></td></tr>
                )}
              </Fragment>
            );
          })}
          {rows.length === 0 && <tr><td colSpan={DOC_KEYS.length + 6} className="text-center py-10 text-slate-400" data-testid="overview-empty">Kayıt bulunamadı</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
